import React from "react";
import { cardClasses, cardStatus } from "@/helpers/style.helpers";
import { QualityCheckAction, ScanActionApiResponseNew } from "@/features/scanners/types";
import { beepOk, beepWarning } from "@/features/scanners/helpers";
import { OrderBag } from "@/types";

interface ComponentProps {
  data: Partial<ScanActionApiResponseNew> | null;
  orderBag?: Partial<OrderBag> | null;
  showScanMessage?: boolean;
}

const ContentComponent: React.FC<ComponentProps> = ({ data, orderBag, showScanMessage }) => {
  const mainCard = data?.action === QualityCheckAction.BAG_PRODUCTS;
  if (!data) {
    if (mainCard || showScanMessage) {
      return <>Scan een zak barcode</>;
    }
    return null;
  }

  const { code } = data;
  const orderbagSequence = data?.orderBag?.sequence || orderBag?.sequence || "";

  switch (code) {
    case undefined:
      return <>Zak: {orderbagSequence}</>;

    case "BAG_DATASTRING":
      return <>Zak: {orderbagSequence}</>;

    case "BIN_DATASTRING":
      return <>Scan een zak barcode</>;

    case "BINS_DO_NOT_MATCH":
      return (
        <div className="flex flex-col items-center justify-center">
          <div>Fout: Verkeerde bak!</div>
          <div>Zak: {orderbagSequence}</div>
        </div>
      );

    case "ORDER_BAG_NOT_FOUND":
      return <>Zak niet gevonden</>;

    case "ORDER_NOT_FOUND":
      if (mainCard) {
        return <>Herstelling niet gevonden</>;
      }
      return null;

    case "INVALID_BARCODE_DATA":
      return <>Ongeldige barcode</>;

    case "INVALID_ORDER_BAG_STATUS":
      return <>Ongeldige zak status</>;

    case "INVALID_ORDER_DELIVERY_METHOD":
      return <>Ongeldige Herstelling leveringsmethode</>;

    case "INVALID_ORDER_PAYMENT_STATUS":
      return <>Ongeldige Herstelling Betaling</>;

    default:
      if (orderBag?.sequence) {
        return <>Zak: {orderbagSequence}</>;
      }
      return null;
  }
};

export const BagCard: React.FC<ComponentProps> = (props) => {
  let className = "";
  const { data } = props;
  // BagCard, BinCard and ProductCard are displayed in pairs. Only one, the top most card, should for example beep on
  // errors. Could be BagCard + ProductCard or BinCard + BagCard.
  const mainCard = data?.action === QualityCheckAction.BAG_PRODUCTS;

  if (data) {
    switch (data.code) {
      case "BIN_DATASTRING":
        if (mainCard) {
          className = cardStatus.info;
          beepOk();
        } else {
          className = cardStatus.neutral;
        }
        break;
      case "BAG_DATASTRING":
      case "BIN_AND_BAG_MATCH":
        beepOk();
        className = cardStatus.info;
        break;
      case "ORDER_NOT_FOUND":
        if (mainCard) {
          className = cardStatus.error;
          beepWarning();
        } else {
          className = cardStatus.neutral;
        }
        break;
      case "ORDER_BAG_NOT_FOUND":
      case "INVALID_ORDER_BAG_STATUS":
      case "INVALID_ORDER_DELIVERY_METHOD":
      case "INVALID_ORDER_PAYMENT_STATUS":
      case "BINS_DO_NOT_MATCH":
        className = cardStatus.error;
        beepWarning();
        break;
      case "INVALID_BARCODE_DATA":
      case "ORDER_BIN_NOT_FOUND":
      case "ORDER_STATUS_TRANSITION_FAILED":
        className = cardStatus.error;
        if (mainCard) {
          beepWarning();
        }
        break;
      default:
        if (data.orderBag) {
          className = cardStatus.info;
        } else {
          className = cardStatus.neutral;
        }
    }
  } else {
    className = cardStatus.neutral;
  }

  return (
    <div className={`${cardClasses} ${className}`}>
      <div className="flex flex-col items-center justify-center">
        {mainCard && data?.qualityCheckProgress === 1 && <div className="m-4 font-semibold">OK</div>}
        <ContentComponent {...props} />
        {mainCard && typeof data?.qualityCheckProgress === "number" && (
          <div className="text-base">progress {data.qualityCheckProgress * 100}%</div>
        )}
        {mainCard && data?.order?.friendlyId && (
          <div className="m-4 text-base">Herstelling: {data.order.friendlyId}</div>
        )}
      </div>
    </div>
  );
};
